import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { useTranslation } from "react-i18next";

import { Form } from "@/components/ui/form";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";

import { DatePickerField, InputField } from "@/features/FormCard/Fields";

import {
  CancelConfirmationModal,
  ConfirmationModal,
} from "@/features/FormCard/Modals";

import { Tables } from "../../../gen/tableNames/tables";
import { Clients } from "../../../lib/clients";

import { FormActionsNew } from "@/features/FormCard/Actions";

import { useEditForm } from "@/features/Edit/hooks";
import { NewDiff } from "@/features/FormCard/NewDiff";
import { dateSchema, parseToDateValue } from "@/features/FormCard/utils";

const FormSchema = z.object({
  date: z.preprocess((val) => parseToDateValue(val), dateSchema).optional(),
  sustainalytics_id: z.preprocess(
    (val) => parseInt(val as string, 10),
    z.number().int(),
  ),
  esg_risk_score: z.coerce.number().nullable().optional(),
  environment_risk_score: z.coerce.number().nullable().optional(),
  social_risk_score: z.coerce.number().nullable().optional(),
  governance_risk_score: z.coerce.number().nullable().optional(),
  country_risk_score: z.coerce.number().nullable().optional(),
  carbon_emissions_scope_1: z.coerce.number().nullable().optional(),
  carbon_emissions_scope_2: z.coerce.number().nullable().optional(),
  carbon_emissions_scope_3: z.coerce.number().nullable().optional(),
  evic_sfdr: z.coerce.number().nullable().optional(),
});

export function G4SustainanalyticsNew() {
  const navigate = useNavigate();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
  } = useEditForm({
    client: Clients.G4,
    table_name: Tables.G4_SUSTAINALYTICS_ESG,
    FormSchema,
    isNew: true,
  });

  const { t } = useTranslation();

  const navigateToList = () => {
    navigate(-1);
  };

  return (
    <div className="grid place-items-center bg-gray-50 h-full w-full">
      <div className="flex justify-center w-full max-w-card">
        <Card className="p-4 bg-white w-full ">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                {t(`${Tables.G4_SUSTAINALYTICS_ESG}.new_metadata`, {
                  ns: "tables",
                })}
              </CardHeader>
              <Divider />

              <CardBody className="flex flex-col gap-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <DatePickerField
                    name="date"
                    label={t(`${Tables.G4_SUSTAINALYTICS_ESG}.columns.date`, {
                      ns: "tables",
                      defaultValue: "Date",
                    })}
                    control={form.control}
                  />
                  <InputField
                    type="number"
                    name="sustainalytics_id"
                    label={t(
                      `${Tables.G4_SUSTAINALYTICS_ESG}.columns.sustainalytics_id`,
                      {
                        ns: "tables",
                      },
                    )}
                    control={form.control}
                    isRequired
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    type="number"
                    name="esg_risk_score"
                    label={t(
                      `${Tables.G4_SUSTAINALYTICS_ESG}.columns.esg_risk_score`,
                      {
                        ns: "tables",
                      },
                    )}
                    control={form.control}
                  />
                  <InputField
                    type="number"
                    name="environment_risk_score"
                    label={t(
                      `${Tables.G4_SUSTAINALYTICS_ESG}.columns.environment_risk_score`,
                      {
                        ns: "tables",
                      },
                    )}
                    control={form.control}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    type="number"
                    name="social_risk_score"
                    label={t(
                      `${Tables.G4_SUSTAINALYTICS_ESG}.columns.social_risk_score`,
                      {
                        ns: "tables",
                      },
                    )}
                    control={form.control}
                  />
                  <InputField
                    type="number"
                    name="governance_risk_score"
                    label={t(
                      `${Tables.G4_SUSTAINALYTICS_ESG}.columns.governance_risk_score`,
                      {
                        ns: "tables",
                      },
                    )}
                    control={form.control}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    type="number"
                    name="country_risk_score"
                    label={t(
                      `${Tables.G4_SUSTAINALYTICS_ESG}.columns.country_risk_score`,
                      {
                        ns: "tables",
                      },
                    )}
                    control={form.control}
                  />
                  <InputField
                    type="number"
                    name="evic_sfdr"
                    label={t(
                      `${Tables.G4_SUSTAINALYTICS_ESG}.columns.evic_sfdr`,
                      {
                        ns: "tables",
                      },
                    )}
                    control={form.control}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    type="number"
                    name="carbon_emissions_scope_1"
                    label={t(
                      `${Tables.G4_SUSTAINALYTICS_ESG}.columns.carbon_emissions_scope_1`,
                      {
                        ns: "tables",
                      },
                    )}
                    control={form.control}
                  />
                  <InputField
                    type="number"
                    name="carbon_emissions_scope_2"
                    label={t(
                      `${Tables.G4_SUSTAINALYTICS_ESG}.columns.carbon_emissions_scope_2`,
                      {
                        ns: "tables",
                      },
                    )}
                    control={form.control}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    type="number"
                    name="carbon_emissions_scope_3"
                    label={t(
                      `${Tables.G4_SUSTAINALYTICS_ESG}.columns.carbon_emissions_scope_3`,
                      {
                        ns: "tables",
                      },
                    )}
                    control={form.control}
                  />
                  <div />
                </div>
              </CardBody>

              <CardFooter>
                <FormActionsNew
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                />
              </CardFooter>
            </form>
          </Form>
        </Card>

        <ConfirmationModal
          isOpen={isConfirmationModalOpen}
          isLoading={isUpdating}
          onClose={cancelUpdate}
          onConfirm={confirmUpdate}
          title={t("confirm_update")}
        >
          {changedFields
            ? NewDiff(changedFields, [], {}, Tables.G4_SUSTAINALYTICS_ESG)
            : t("no_changes")}
        </ConfirmationModal>

        <CancelConfirmationModal
          isOpen={isDeleteModalOpen}
          onClose={closeDeleteModal}
          onCancel={navigateToList}
        />
      </div>
    </div>
  );
}
